import React, { useState, useEffect } from "react";
import axios from "axios";
import { FileText, FileSpreadsheet, ExternalLink, CheckCircle2, Lock, Building2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvestorSharePage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "" });
  const [status, setStatus] = useState("gate"); // gate | loading | unlocked | error
  const [links, setLinks] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    // If a viewer previously unlocked in this browser, skip the gate.
    try {
      const cached = JSON.parse(localStorage.getItem("investor_share_unlock") || "null");
      if (cached && cached.business_plan_pdf_url) {
        setLinks(cached);
        setStatus("unlocked");
      }
    } catch (_) {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/investor/share/unlock`, form);
      // Backend returns relative paths — prepend current origin so links
      // work when the SPA and API share the same public URL.
      const origin = window.location.origin;
      const normalised = {
        ...data,
        business_plan_pdf_url: data.business_plan_pdf_url.startsWith("http") ? data.business_plan_pdf_url : `${origin}${data.business_plan_pdf_url}`,
        proforma_xlsx_url: data.proforma_xlsx_url.startsWith("http") ? data.proforma_xlsx_url : `${API}${data.proforma_xlsx_url}`,
        portal_url: data.portal_url.startsWith("http") ? data.portal_url : `${origin}${data.portal_url}`,
      };
      localStorage.setItem("investor_share_unlock", JSON.stringify(normalised));
      setLinks(normalised);
      setStatus("unlocked");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not unlock — please try again.");
      setStatus("error");
    }
  };

  if (status === "unlocked" && links) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="investor-share-unlocked">
        <header className="border-b border-slate-200 bg-white sticky top-0 z-10 shadow-sm">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
            <Building2 className="w-6 h-6 text-blue-900" />
            <div>
              <p className="font-bold text-slate-900">Sterile Mate LLC</p>
              <p className="text-xs text-slate-500">Confidential Investor Package</p>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm mb-6">
            <div className="flex items-center gap-2 text-emerald-700 mb-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-xs font-bold uppercase tracking-widest">Access Granted</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Sterile Mate LLC — Investor Package</h1>
            <p className="text-slate-700 mb-6">
              Choose the document you want to review. All materials are confidential and intended for the named recipient.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {/* Full Business Plan (Portal) */}
              <a
                href={links.portal_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group border border-slate-200 rounded-xl p-6 hover:border-blue-900 hover:shadow-md transition"
                data-testid="share-open-portal"
              >
                <div className="w-10 h-10 rounded bg-blue-900 flex items-center justify-center mb-3">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-slate-900 mb-1">Full Business Plan</p>
                <p className="text-sm text-slate-600 mb-3">Interactive investor portal — Executive Summary, Market Analysis, Financial Projections, Management.</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-900 group-hover:gap-2 transition-all">
                  Open portal <ArrowRight className="w-4 h-4" />
                </span>
              </a>

              {/* Business Plan PDF */}
              <a
                href={links.business_plan_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group border border-slate-200 rounded-xl p-6 hover:border-blue-900 hover:shadow-md transition"
                data-testid="share-download-pdf"
              >
                <div className="w-10 h-10 rounded bg-blue-900 flex items-center justify-center mb-3">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-slate-900 mb-1">Business Plan PDF</p>
                <p className="text-sm text-slate-600 mb-3">Downloadable investor package — Executive Summary through Financial Model.</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-900 group-hover:gap-2 transition-all">
                  Download PDF <ArrowRight className="w-4 h-4" />
                </span>
              </a>

              {/* Proforma XLSX */}
              <a
                href={links.proforma_xlsx_url}
                className="group border border-slate-200 rounded-xl p-6 hover:border-blue-900 hover:shadow-md transition sm:col-span-2"
                data-testid="share-download-proforma"
              >
                <div className="w-10 h-10 rounded bg-blue-900 flex items-center justify-center mb-3">
                  <FileSpreadsheet className="w-5 h-5 text-white" />
                </div>
                <p className="font-bold text-slate-900 mb-1">5-Year Pro-Forma (Excel)</p>
                <p className="text-sm text-slate-600 mb-3">Full P&L, DSCR, private-label glove economics, microfiber math — all cells editable.</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-blue-900 group-hover:gap-2 transition-all">
                  Download XLSX <ArrowRight className="w-4 h-4" />
                </span>
              </a>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 text-xs text-slate-500">
            <span className="font-semibold text-slate-700">Confidentiality Notice:</span> The information contained in this
            package is proprietary and confidential to Sterile Mate LLC. Please do not forward, copy, or share externally
            without written consent from the CEO.
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4" data-testid="investor-share-gate">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
        <div className="bg-blue-900 px-6 py-4 flex items-center gap-3">
          <Lock className="w-5 h-5 text-white" />
          <div>
            <p className="text-xs font-bold text-blue-100 uppercase tracking-widest">Confidential Investor Package</p>
            <p className="text-white font-semibold">Sterile Mate LLC — Plan C</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            You&rsquo;ve been invited to review Sterile Mate&rsquo;s investor package. Please introduce yourself and we&rsquo;ll unlock the business plan and pro-forma.
          </p>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-widest">Full Name *</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:border-blue-900 text-slate-900"
              placeholder="Jane Doe"
              data-testid="share-input-name"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-widest">Email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:border-blue-900 text-slate-900"
              placeholder="jane@example.com"
              data-testid="share-input-email"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-widest">Company (optional)</label>
            <input
              type="text"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:border-blue-900 text-slate-900"
              placeholder="Frost Bank, Family Office, etc."
              data-testid="share-input-company"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-widest">Role (optional)</label>
            <input
              type="text"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded focus:outline-none focus:border-blue-900 text-slate-900"
              placeholder="SBA Underwriter, Investor, Advisor"
              data-testid="share-input-role"
            />
          </div>
          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
          )}
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full bg-blue-900 hover:bg-blue-800 text-white font-semibold py-2.5 rounded transition disabled:opacity-60"
            data-testid="share-unlock-button"
          >
            {status === "loading" ? "Unlocking…" : "Unlock Investor Package"}
          </button>
          <p className="text-[11px] text-slate-500 text-center">
            By continuing you agree that this material is confidential and intended solely for your review.
          </p>
        </form>
      </div>
    </div>
  );
}
